import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/App";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { 
  BookOpen, 
  Clock, 
  ChevronRight,
  CheckCircle2,
  ArrowLeft,
  Target
} from "lucide-react";

const SubjectLearnPage = () => {
  const navigate = useNavigate();
  const { subjectId } = useParams();
  const [subject, setSubject] = useState(null);
  const [studiedTopics, setStudiedTopics] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [subjectData, progressData] = await Promise.all([
          api.get(`/learn/${subjectId}`),
          api.get("/learn/progress")
        ]);
        setSubject(subjectData.subject);
        setStudiedTopics(progressData.studied_topics || []);
      } catch (error) {
        console.error("Failed to fetch subject:", error);
        navigate("/learn");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [subjectId, navigate]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!subject) {
    return null;
  }

  const completedCount = subject.topics?.filter(t => 
    studiedTopics.includes(`${subjectId}/${t.id}`)
  ).length || 0;
  const totalTopics = subject.topics?.length || 0;
  const progress = totalTopics > 0 ? (completedCount / totalTopics) * 100 : 0;

  return (
    <div data-testid="subject-learn-page" className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/learn")}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Learn
        </Button>

        <div className="glass rounded-2xl p-6 border border-border">
          <div className="flex items-start gap-4">
            <div 
              className="w-16 h-16 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: `${subject.color}20` }}
            >
              <BookOpen className="w-8 h-8" style={{ color: subject.color }} />
            </div>
            <div className="flex-1">
              <h1 className="text-2xl font-bold mb-1">{subject.name}</h1>
              <p className="text-muted-foreground mb-4">{subject.description}</p>
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-muted-foreground">Progress</span>
                    <span className="font-medium">{completedCount}/{totalTopics} topics</span>
                  </div>
                  <Progress value={progress} className="h-2" />
                </div>
                <Button
                  onClick={() => navigate(`/practice/${subjectId}`)}
                  variant="outline"
                  className="rounded-full"
                >
                  <Target className="w-4 h-4 mr-2" />
                  Practice
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Topics List */}
      <div className="space-y-4">
        <h2 className="text-xl font-semibold">Topics</h2>
        
        {subject.topics?.map((topic, index) => {
          const isCompleted = studiedTopics.includes(`${subjectId}/${topic.id}`);
          
          return (
            <button
              key={topic.id}
              onClick={() => navigate(`/learn/${subjectId}/${topic.id}`)}
              className="w-full glass rounded-xl p-5 border border-border hover:border-border transition-all text-left hover-lift"
            >
              <div className="flex items-start gap-4">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                  isCompleted ? 'bg-success/20' : 'bg-secondary'
                }`}>
                  {isCompleted ? (
                    <CheckCircle2 className="w-5 h-5 text-success" />
                  ) : (
                    <span className="text-sm font-medium text-muted-foreground">{index + 1}</span>
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="font-semibold">{topic.name}</h3>
                    <ChevronRight className="w-5 h-5 text-muted-foreground" />
                  </div>
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {topic.estimated_time} min
                    </span>
                    <span className="px-2 py-0.5 rounded bg-secondary">
                      {topic.difficulty}
                    </span>
                    <span>{topic.sections?.length || 0} sections</span>
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default SubjectLearnPage;
